import { useRef } from 'react';
import SEOMetadata from '@/components/SEOMetadata';
import IndonesiaHeader from '@/components/indonesia/IndonesiaHeader';
import IndonesiaServicesGrid from '@/components/indonesia/IndonesiaServicesGrid';
import IndonesiaFooter from '@/components/indonesia/IndonesiaFooter';
import FreeProxyPopup, { FreeProxyPopupRef } from '@/components/FreeProxyPopup';

const IndonesiaSplash = () => {
  const freeProxyPopupRef = useRef<FreeProxyPopupRef>(null);

  const handleGetFreeAccess = () => {
    freeProxyPopupRef.current?.openPopup();
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Layanan Internet AS untuk Indonesia",
    "description": "Akses proxy residensial AS, VPN, dan desktop jarak jauh dengan harga terjangkau. Solusi internet terpercaya untuk pengguna Indonesia.",
    "provider": {
      "@type": "Organization",
      "name": "NEXX TELECOM LLC"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Indonesia"
    },
    "offers": {
      "@type": "Offer",
      "description": "Solusi internet global untuk Indonesia"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 relative overflow-hidden">
      <SEOMetadata 
        title="Layanan Internet AS untuk Indonesia | NEXX - Solusi Proxy & VPN"
        description="Solusi internet residensial AS terbaik untuk pengguna Indonesia. Proxy, VPN, dan Remote Desktop dengan koneksi cepat dan aman. Harga terjangkau!"
        keywords="Indonesia internet, proxy Indonesia, VPN Indonesia, US residential proxy, remote desktop Indonesia, layanan internet AS"
        canonical="https://nexx.net/indonesia"
        structuredData={structuredData}
      />

      {/* Indonesian Flag-colored top border */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-red-600 via-red-600 to-white z-30"></div>

      {/* Enhanced Animated Background */}
      <div className="absolute inset-0 overflow-hidden z-10">
        {/* Floating geometric shapes in Indonesian flag colors */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-red-600/10 rounded-full animate-pulse" style={{ animationDelay: '0.5s', animationDuration: '4s' }}></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-white/40 rounded-lg rotate-45 animate-bounce border border-red-600/20" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
        <div className="absolute bottom-40 left-20 w-20 h-20 bg-red-600/15 rounded-full animate-ping" style={{ animationDelay: '1.5s', animationDuration: '4s' }}></div>
        <div className="absolute bottom-20 right-40 w-16 h-16 bg-red-600/20 rounded-lg animate-pulse" style={{ animationDelay: '2s', animationDuration: '3s' }}></div>
        <div className="absolute top-1/3 left-1/4 w-8 h-8 bg-white/50 rounded-full animate-bounce border border-red-600/30" style={{ animationDelay: '0.8s', animationDuration: '5s' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-12 h-12 bg-red-600/10 rounded-lg rotate-12 animate-pulse" style={{ animationDelay: '2.5s', animationDuration: '3.5s' }}></div>
        
        {/* Small floating dots */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-red-600/25 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 flex flex-col min-h-screen">
        <div className="flex-grow flex flex-col justify-center p-4 max-w-7xl mx-auto w-full">
          <IndonesiaHeader />
          <IndonesiaServicesGrid onGetFreeAccess={handleGetFreeAccess} />
          
          {/* Trust Message */}
          <div className="text-center mb-8 mt-6">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
              <span className="text-lg animate-pulse">🇮🇩</span>
              <span className="ml-3 font-bold">Dibuat untuk pengguna Indonesia • Built for Indonesian users</span>
            </div>
          </div>
        </div>
        <IndonesiaFooter />
      </div>

      <FreeProxyPopup ref={freeProxyPopupRef} />
    </div>
  );
};

export default IndonesiaSplash;
